/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanMatch, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { UserProfile } from '@models/UserProfile';
import { OktaAuthStateService } from '@okta/okta-angular';
import { ConfigService } from '@services/config.service';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment as env } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class OpsGuard implements CanMatch, CanActivate {
  constructor(
    private readonly stateService: OktaAuthStateService,
    private readonly router: Router,
    private readonly configService: ConfigService,
  ) {}

  hasBrandRole(brand: string): Observable<boolean> {
    return this.configService.CurrentProfile$.pipe(
      take(1),
      map((profile: UserProfile) => {
        // Bail early if we're in ops but not in a brand section (landing page or other "public" page)
        if (brand === '') {
          return true;
        }

        // Check that the brand param in the URL exists for this user
        const hasOpsProfile = profile.opsProfile != null;
        const hasGlobalRole = hasOpsProfile && (profile.opsProfile.globalRole ?? '') !== '';
        const hasOpsRoleForBrand = hasOpsProfile && (profile.opsProfile.brandRoles || []).some((p: any) => p.brandCode === brand);

        return hasGlobalRole || hasOpsRoleForBrand;
      }),
    );
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (!route.params.brand) {
      return true;
    }

    return this.hasBrandRole(route.params.brand).pipe(
      map((hasBrandRole) => {
        if (hasBrandRole) {
          return true;
        }
        this.router.navigate(['/']);
        return false;
      }),
    );
  }

  canMatch(route: Route, segments: UrlSegment[]): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    // Wait for Okta auth state to be fully initialized before checking permissions
    // The authState$ observable uses startWith(false), so we filter to wait for real auth state
    // Only proceed when we have evidence that Okta has loaded (tokens present or isAuthenticated is true)
    return this.stateService.authState$.pipe(
      filter((authState) => {
        const hasTokens = !!(authState.accessToken || authState.idToken);
        return authState.isAuthenticated === true || hasTokens;
      }),
      take(1),
      switchMap((authState) => {
        if (!authState.isAuthenticated) {
          this.router.navigate(['/']);
          return [false];
        }

        return this.stateService.hasAnyGroups(env.opsRole).pipe(
          take(1),
          map((hasOpsRole) => {
            if (hasOpsRole) {
              return true;
            }
            this.router.navigate(['/']);
            return false;
          }),
        );
      }),
    );
  }
}
